import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import capitalize from 'lodash/capitalize';
import compact from 'lodash/compact';
import isBoolean from 'lodash/isBoolean';
import get from 'lodash/get';
import map from 'lodash/map';
import { format as currencyFormat } from 'currency-formatter';
import DashboardBreadcrumb from '../DashboardBreadcrumb/DashboardBreadcrumb';
import ModalPortal from '../ModalPortal/ModalPortal';
import FormattedAddress from '../FormattedAddress';
import * as analytics from '../../services/analytics';
import './Passport360PolicyDetailsView.css';

const propTypes = {
  client: PropTypes.object,
};

class Passport360PolicyDetailsView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      modalTitle: null,
      modalBody: null,
    };
  }

  componentDidMount() {
    analytics.page('Broker Portal - Policy Details');
    this.props.getPolicies({ clientId: this.props.client.id });
  }

  getBadgeForStatus(status) {
    switch (status) {
      case 'inforce':
        return (
          <span className="badge badge-success text-uppercase">In force</span>
        );
      case 'canceled':
        return (
          <span className="badge badge-danger text-uppercase">Canceled</span>
        );
      default:
        return 'n/a';
    }
  }

  getCoverageDetailsValue(value) {
    if (isBoolean(value)) {
      return value ? 'Included' : 'Not Included';
    } else {
      return value || 'n/a';
    }
  }

  showInfoModal = (e) => {
    analytics.track('Broker Portal - Coverage Details Modal Opened', {
      title: e.currentTarget.dataset.modalTitle,
    });
    this.setState(
      {
        modalTitle: e.currentTarget.dataset.modalTitle,
        modalBody: e.currentTarget.dataset.modalBody,
      },
      () => {
        window.$(this.modal).modal('show');
      }
    );
  };

  trackPolicyDocClick = (e) => {
    analytics.track('Broker Portal - External Link Clicked', {
      label: e.target.dataset.docName,
    });
  };

  get coverageDetails() {
    const { policy } = this.props;
    return compact(
      map(policy.plan.settings.details.coverage_details, (coverage, key) => {
        if (coverage) {
          return (
            <tr key={key}>
              <td>
                {get(coverage, 'title')}{' '}
                {coverage.description && (
                  <span
                    data-modal-title={get(coverage, 'title')}
                    data-modal-body={coverage.description}
                    onClick={this.showInfoModal}
                  >
                    <i className="Passport360PolicyDetailsView-infoIcon fas fa-info-circle" />
                  </span>
                )}
              </td>
              <td>{this.getCoverageDetailsValue(coverage.value)}</td>
            </tr>
          );
        } else {
          return null;
        }
      })
    );
  }

  render() {
    const { client, policy } = this.props;
    const bill = get(policy, 'bills[0]');
    const currencyOpts = {
      code: bill.currency.code,
    };

    return (
      <Fragment>
        <div className="Passport360PolicyDetailsView">
          <DashboardBreadcrumb client={client} policy={policy} />
          <div className="container-fluid">
            <h2 className="Passport360PolicyDetailsView-heading">
              Policy details
            </h2>
            <div className="row">
              <div className="col-md-4">
                <div className="card">
                  <h3 className="Passport360PolicyDetailsView-sectionTitle">
                    Overview
                  </h3>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td>Policy number</td>
                        <td>{policy.policy_number || 'n/a'}</td>
                      </tr>
                      <tr>
                        <td>Date issued</td>
                        <td>
                          {moment(policy.created_at).format('MMM DD, YYYY')}
                        </td>
                      </tr>
                      <tr>
                        <td>Term</td>
                        <td>
                          {moment(policy.starts_at, 'YYYY-MM-DD').format(
                            'MMM DD, YYYY'
                          )}{' '}
                          to{' '}
                          {moment(policy.ends_at, 'YYYY-MM-DD').format(
                            'MMM DD, YYYY'
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Status</td>
                        <td>{this.getBadgeForStatus(policy.status)}</td>
                      </tr>
                      {policy.payment_method && (
                        <tr>
                          <td>Payment method</td>
                          <td>{policy.payment_method.name}</td>
                        </tr>
                      )}
                      {map(policy.policy_documents, (doc, i) => (
                        <tr key={i}>
                          <td>{doc.name}</td>
                          <td>
                            <a
                              className="text-complete"
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-doc-name={doc.name}
                              onClick={this.trackPolicyDocClick}
                            >
                              <i className="fas fa-download" /> Download
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-md-8">
                <div className="card">
                  <h3 className="Passport360PolicyDetailsView-sectionTitle">
                    Policy holder details
                  </h3>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Mailing address</th>
                        <th>Date of birth</th>
                        <th>Email</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {policy.policy_holders.map((policyholder) => (
                        <tr key={policyholder.person.id}>
                          <td>
                            {policyholder.person.first_name}{' '}
                            {policyholder.person.last_name}
                          </td>
                          <td>
                            <FormattedAddress
                              address={policyholder.person.address}
                            />
                          </td>
                          <td>
                            {(get(policyholder, 'person.dob') &&
                              moment(
                                get(policyholder, 'person.dob'),
                                'YYYY-MM-DD'
                              ).format('MM/DD/YYYY')) ||
                              'n/a'}
                          </td>
                          <td>{policyholder.person.email}</td>
                          <td>{policyholder.person.phone_number}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="card">
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td
                          colSpan="2"
                          className="Passport360PolicyDetailsView-sectionTitle"
                        >
                          Coverage details
                        </td>
                      </tr>
                      <tr>
                        <td>Plan type</td>
                        <td>
                          {capitalize(policy.plan.settings.details.plan_type)}
                        </td>
                      </tr>
                      <tr>
                        <td>Plan level</td>
                        <td>
                          {capitalize(policy.plan.settings.details.plan_name)}
                        </td>
                      </tr>
                      {this.coverageDetails}

                      {map(
                        policy.plan.settings.details.optional_coverages,
                        (coverage) => {
                          return (
                            coverage && (
                              <Fragment>
                                <tr>
                                  <td
                                    colSpan="2"
                                    className="Passport360PolicyDetailsView-sectionTitle"
                                  >
                                    Upgrade
                                  </td>
                                </tr>
                                <tr>
                                  <td>Plan name</td>
                                  <td>{capitalize(get(coverage, 'title'))}</td>
                                </tr>
                                {map(coverage.benefits, (benefit) => (
                                  <tr key={benefit.title}>
                                    <td>
                                      {benefit.title}{' '}
                                      {benefit.description && (
                                        <span
                                          data-modal-title={benefit.title}
                                          data-modal-body={benefit.description}
                                          onClick={this.showInfoModal}
                                        >
                                          <i className="Passport360PolicyDetailsView-infoIcon fas fa-info-circle" />
                                        </span>
                                      )}
                                    </td>
                                    <td>
                                      {this.getCoverageDetailsValue(
                                        benefit.value
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                <tr>
                                  <td>Plan total</td>
                                  <td>
                                    {currencyFormat(
                                      coverage.price,
                                      currencyOpts
                                    )}
                                  </td>
                                </tr>
                              </Fragment>
                            )
                          );
                        }
                      )}

                      <tr>
                        <td
                          colSpan="2"
                          className="Passport360PolicyDetailsView-sectionTitle"
                        >
                          Price
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Total plan price</strong>
                        </td>
                        <td>
                          {currencyFormat(bill.total_amount, currencyOpts)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="card">
                  <h3 className="Passport360PolicyDetailsView-sectionTitle">
                    Beneficiaries
                  </h3>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Address</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Percentage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {policy.beneficiaries.map((beneficiary) => (
                        <tr key={beneficiary.person.id}>
                          <td>
                            {beneficiary.person.first_name}{' '}
                            {beneficiary.person.last_name}
                          </td>
                          <td>
                            <FormattedAddress
                              address={beneficiary.person.address}
                            />
                          </td>
                          <td>{beneficiary.person.email}</td>
                          <td>{beneficiary.person.phone_number}</td>
                          <td>{beneficiary.percentage}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ModalPortal>
          <div className="modal" id="infoModal" ref={(c) => (this.modal = c)}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content-wrapper">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.modalTitle}</h5>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <p>{this.state.modalBody}</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-complete"
                      data-dismiss="modal"
                    >
                      Ok
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      </Fragment>
    );
  }
}

Passport360PolicyDetailsView.propTypes = propTypes;

export default Passport360PolicyDetailsView;
